import React, { useState, useEffect } from 'react';
import { Box, Grid, useTheme, useMediaQuery, Backdrop } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { 
  ProfileContainer, 
  TabPanel, 
  ContentPanel,
} from './PostalOperators.styled';
import ModulePreferences from './components/ModulePreferences';
import UserPreferences from '../UserPreferences';
import UserManagement from './components/UserManagement';
import ImpcCodes from './components/ImpcCodes';
import DesignatedOperators from './components/DesignatedOperators';
import { 
  ModulePreferencesIcon, 
  ImpcCodesIcon, 
  UserManagementIcon,
  DesignatedOperatorsIcon,
  UserPreferencesIcon
} from './components/TabIcons';
import CustomTab from './components/CustomTab';
import CustomTabs from './components/CustomTabs';
import { useNavigate, useLocation } from 'react-router-dom';
import { Sidebar } from '../../common/components/Sidebar';
import { useNavItems } from '../../hooks';
import SectionBar from '../../common/components/SectionBar/SectionBar';
import { useUser } from '../../hooks/useUser';
import { UserRoles } from '../../common/constants/userConstants';

const Profile: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  // Get current user info to check for admin role
  const { data: userInfo } = useUser();
  const isAdmin = userInfo?.role === UserRoles.ADMIN;
  const isAdminOrSuperUser = userInfo?.role === UserRoles.ADMIN || userInfo?.role === UserRoles.SUPER_USER;
  
  // Sidebar state
  const [sidebarOpen, setSidebarOpen] = useState(true);
  
  // Close sidebar on mobile by default
  useEffect(() => {
    if (isMobile) {
      setSidebarOpen(false);
    }
  }, [isMobile]);
  
  // Get navigation items
  const navItems = useNavItems();
  
  // Initialize tab from URL parameter or default based on role
  const params = new URLSearchParams(location.search);
  const defaultTab = isAdminOrSuperUser ? 0 : 3; // Regular users default to User Preferences (tab 3)
  const initialTab = parseInt(params.get('activeTab') || String(defaultTab), 10);
  const [activeTab, setActiveTab] = useState(initialTab);
  
  // Function to change tabs and update URL
  const handleTabChange = (tabIndex: number) => {
    // Only update if the tab is changing
    if (tabIndex === activeTab) return;
    
    // Update state
    setActiveTab(tabIndex);
    
    // Update URL preserving other query parameters
    const params = new URLSearchParams(location.search);
    
    if (tabIndex === 0) {
      params.delete('activeTab');
    } else {
      params.set('activeTab', tabIndex.toString());
    }
    
    navigate({
      pathname: location.pathname,
      search: params.toString() ? `?${params.toString()}` : ''
    }, { replace: true });
  };

  const handleSidebarToggle = () => {
    setSidebarOpen(!sidebarOpen);
  };
  
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }} data-testid="profile-container">
      <Grid container>
        {/* First Row - SectionBar */}
        <Grid item xs={12}>
          <SectionBar
            title="Postal Operators"
            onToggleSidebar={handleSidebarToggle}
            isSidebarOpen={sidebarOpen}
            showFilters={{
              status: false,
              type: false,
              year: false,
              ppo: false
            }}
          />
        </Grid>

        {/* Second Row - Sidebar and MainContent */}
        <Grid item xs={12} sx={{ display: 'flex', position: 'relative' }}>
          {/* Sidebar */}
          {sidebarOpen && (
            <Box sx={{ position: 'relative' }}>
              <Sidebar 
                isOpen={sidebarOpen} 
                onToggle={handleSidebarToggle}
                navItems={navItems}
              />
              {isMobile && sidebarOpen && (
                <Backdrop
                  open={true}
                  onClick={handleSidebarToggle}
                  sx={{ zIndex: 1100, backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
                  data-testid="profile-mobile-backdrop"
                />
              )}
            </Box>
          )}

          {/* Main Content */}
          <Box sx={{ 
            flexGrow: 1, 
            width: sidebarOpen ? 'calc(100% - 240px)' : '100%',
            ml: 0,
            transition: 'margin-left 0.3s ease-out, width 0.3s ease-out'
          }}>
            <ProfileContainer>
              <Box sx={{ width: '100%' }} data-testid="profile-box">
                <CustomTabs>
                  {isAdminOrSuperUser && (
                    <CustomTab 
                      icon={<ModulePreferencesIcon />}
                      label={t('profile.tabs.modulePreferences', 'Module Preferences')}
                      isActive={activeTab === 0}
                      onClick={() => handleTabChange(0)}
                      data-testid="profile-tab-module-preferences"
                    />
                  )}
                  {(
                    <CustomTab 
                      icon={<ImpcCodesIcon />}
                      label={t('profile.tabs.ipcCodes', 'IMPC Codes')}
                      isActive={activeTab === 1}
                      onClick={() => handleTabChange(1)}
                      data-testid="profile-tab-impc-codes"
                    />
                  )}
                  {isAdminOrSuperUser && (
                    <CustomTab 
                      icon={<UserManagementIcon />}
                      label={t('profile.tabs.userManagement', 'User Management')}
                      isActive={activeTab === 2}
                      onClick={() => handleTabChange(2)}
                      data-testid="profile-tab-user-management"
                    />
                  )}
                  <CustomTab 
                    icon={<UserPreferencesIcon />}
                    label={t('profile.tabs.userPreferences', 'User Preferences')}
                    isActive={activeTab === 3}
                    onClick={() => handleTabChange(3)}
                    data-testid="profile-tab-user-preferences"
                  />
                  {/* Only show Designated Operators tab for admin users */}
                  {isAdmin && (
                    <CustomTab 
                      icon={<DesignatedOperatorsIcon />}
                      label={t('profile.tabs.designatedOperators', 'Designated Operators')}
                      isActive={activeTab === 4}
                      onClick={() => handleTabChange(4)}
                      data-testid="profile-tab-designated-operators"
                    />
                  )}
                </CustomTabs>
                
                <TabPanel data-testid="profile-tab-panel">
                  {activeTab === 0 && isAdminOrSuperUser && (
                    <ContentPanel data-testid="profile-content-module-preferences">
                      <ModulePreferences />
                    </ContentPanel>
                  )}
                  
                  {activeTab === 1 && (
                    <ContentPanel data-testid="profile-content-impc-codes">
                      <ImpcCodes />
                    </ContentPanel>
                  )}
                  
                  {activeTab === 2 && isAdminOrSuperUser && (
                    <ContentPanel data-testid="profile-content-user-management">
                      <UserManagement isCodesTab={false} />
                    </ContentPanel>
                  )}
                  
                  {activeTab === 3 && (
                    <ContentPanel data-testid="profile-content-user-preferences">
                      <UserPreferences />
                    </ContentPanel>
                  )}
                  
                  {activeTab === 4 && isAdmin && (
                    <ContentPanel data-testid="profile-content-designated-operators">
                      <DesignatedOperators />
                    </ContentPanel>
                  )}
                </TabPanel>
              </Box>
            </ProfileContainer>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Profile; 